import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Paper, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Data from './Data';
import GlobalToast from '../../../components/Toasts/GlobalToast';
import axios from 'axios';

const ChooseUs = lazy(() => import('./ChooseUs'));
const Diffrance = lazy(() => import('./Diffrance'));
const Brand = lazy(() => import('./Brand'));

function getYouTubeEmbedUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  const embedMatch = u.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]+)/);
  if (embedMatch) return `https://www.youtube.com/embed/${embedMatch[1]}`;
  const watchMatch = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
  const shortsMatch = u.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{6,})/i);
  if (shortsMatch) return `https://www.youtube.com/embed/${shortsMatch[1]}`;
  return null;
}

const DESKTOP_VIDEO = 'https://bxidevelopment1.s3.ap-south-1.amazonaws.com/BxiStatic/bxilayoutvideo.mp4';
const MOBILE_VIDEO = 'https://bxidevelopment1.s3.ap-south-1.amazonaws.com/BxiStatic/bximobilebanner.mp4';
const MOBILE_BANNER_QUERY = '(max-width: 899px)';

function isMobileBanner() {
  return typeof window !== 'undefined' && window.matchMedia?.(MOBILE_BANNER_QUERY)?.matches;
}

function HomeBannerVideo() {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const videoRef = useRef(null);
  const fileUrl = isMobileViewport ? MOBILE_VIDEO : DESKTOP_VIDEO;
  // Phone Lighthouse treats the hero as LCP. Start that S3 file on the first paint.
  // Desktop keeps the heading as LCP and starts the larger file after the page is up.
  const [playSrc, setPlaySrc] = useState(() => (isMobileBanner() ? MOBILE_VIDEO : null));
  const embedUrl = getYouTubeEmbedUrl(fileUrl);
  const title = isMobileViewport ? 'Mobile banner video' : 'Desktop banner video';

  useEffect(() => {
    if (isMobileViewport) {
      setPlaySrc(MOBILE_VIDEO);
      return undefined;
    }
    let cancelled = false;
    let idleId;
    let timer;
    const start = () => {
      if (!cancelled) setPlaySrc(DESKTOP_VIDEO);
    };
    const arm = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(start, { timeout: 1500 });
      } else {
        timer = setTimeout(start, 800);
      }
    };
    if (document.readyState === 'complete') arm();
    else window.addEventListener('load', arm, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener('load', arm);
      if (idleId && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleId);
      if (timer) clearTimeout(timer);
    };
  }, [isMobileViewport]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !playSrc) return undefined;
    const play = () => {
      el.play?.().catch(() => {});
    };
    play();
    el.addEventListener('loadeddata', play);
    return () => el.removeEventListener('loadeddata', play);
  }, [playSrc]);

  if (embedUrl) {
    return (
      <Box sx={{ position: 'relative', width: '100%', pt: '56.25%', overflow: 'hidden' }}>
        <iframe
          src={embedUrl}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden',
        bgcolor: '#111111',
        aspectRatio: { xs: '1080 / 800', md: '1912 / 750' },
      }}
    >
      <video
        key={fileUrl}
        ref={videoRef}
        autoPlay={Boolean(playSrc)}
        loop
        muted
        playsInline
        preload={playSrc && isMobileViewport ? 'auto' : 'none'}
        fetchPriority={isMobileViewport ? 'high' : 'low'}
        src={playSrc || undefined}
        width={isMobileViewport ? 1080 : 1912}
        height={isMobileViewport ? 800 : 750}
        style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
      />
    </Box>
  );
}

const MainHome = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    company: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;
      const scrollPercentage = (scrollTop + windowHeight) / fullHeight;
      if (scrollPercentage >= 0.9 && !open && localStorage.getItem('enrqiry') !== 'sent') {
        setOpen(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const restrictedDomains = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com'];

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    } else if (restrictedDomains.some(domain => formData.email.endsWith(`@${domain}`))) {
      newErrors.email = 'Personal email domains are not allowed';
    }

    const phoneRegex = /^[0-9]{10,11}$/;
    if (!formData.phone) {
      newErrors.phone = 'phone number is required';
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Enter a valid phone number (10–11 digits)';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const response = await axios.post('demoschedular/request_enquiry', { formData }).then((response) => {
        if (response?.data?.success) {
          localStorage.setItem('enrqiry', 'sent');
          setOpen(false);
        }
      });

      if (response.ok) {
        setOpen(false);
      } else {
        GlobalToast('Submission failed ❌', 'error');
      }
    } catch (err) {
      GlobalToast(err, 'error');
    }
  };

  const handleClose = () => {
    setOpen(false);
    localStorage.setItem('enrqiry', 'sent');
  };

  return (
    <Paper elevation={0} sx={{ overflow: 'visible', width: '100%' }}>
      <Box sx={{ width: '100%' }}>
        <HomeBannerVideo />
      </Box>
      <Data />
      <Suspense fallback={null}>
        <ChooseUs />
        <Diffrance />
        <Brand />
      </Suspense>
    </Paper>
  );
};

export default MainHome;
